import { v4 as uuidv4 } from 'uuid';
import { lAsync } from 'app/blocks/common/codes';
import { confirm, confirmType } from 'app/blocks/Modal/utils';
import ConfirmationDialog from './article-affiliations-panel/Institutions/Dialog/ConfirmationDialog';
import { PriceChangeType } from './types';

const CDL_SCELC_WORKFLOW_TYPES = ['CDL', 'SCELC'];
const NORMAL_WORKFLOW_TYPE = 'Normal';

type EditorAffiliation = ArticleAffiliation & {
    confirmed: boolean;
    emailMatched?: string;
    hideEligibilityMessage?: boolean;
    id: string;
    suggestions?: Institution[];
};

type PriceChangeDecisionInput = {
    affiliations: ArticleAffiliation[];
    fundingGroupId?: string;
    isAuthorPaid: boolean;
    journalRevenueModel?: string;
    isWaiverAvailable: boolean;
    selectedPrimaryAffiliationId?: string;
    affiliationsConfirmed: boolean;
    woaCode?: string;
    rejectedAccounts?: RejectedAccount[];
};

type PriceChangeDecisionResult = {
    eligibleAffiliations: ArticleAffiliation[];
    priceChangeType?: PriceChangeType;
    rejectionReasonAccountBalanceNotEnough?: boolean;
    rejectionReasonAccountBalanceNotEnoughInstitutionName?: string;
    shouldShowPriceChangeConfirmation: boolean;
};

async function confirmAffiliationDeletion(name: string, isEligible: boolean) {
    if (isEligible) {
        return confirmType(ConfirmationDialog, {
            cancelBtnLabel: await lAsync('ARTICLE_AFFILIATION_EDITOR.DELETE_ELIGIBLE_BTN_REJECT'),
            confirmBtnLabel: await lAsync('ARTICLE_AFFILIATION_EDITOR.DELETE_ELIGIBLE_BTN_CONFIRM'),
            message: await lAsync('ARTICLE_AFFILIATION_EDITOR.DELETE_ELIGIBLE_CONFIRMATION_TEXT', { name }),
            title: await lAsync('ARTICLE_AFFILIATION_EDITOR.DELETE_ELIGIBLE_CONFIRMATION_TITLE'),
            useButtonForConfirm: true,
        });
    }

    return confirm(await lAsync('ARTICLE_AFFILIATION_EDITOR.DELETE_CONFIRMATION_TEXT', { name }));
}

async function confirmAffiliationEditing(name: string, isEligible: boolean) {
    if (isEligible) {
        return confirmType(ConfirmationDialog, {
            cancelBtnLabel: await lAsync('ARTICLE_AFFILIATION_EDITOR.EDIT_ELIGIBLE_BTN_REJECT'),
            confirmBtnLabel: await lAsync('ARTICLE_AFFILIATION_EDITOR.EDIT_ELIGIBLE_BTN_CONFIRM'),
            message: await lAsync('ARTICLE_AFFILIATION_EDITOR.EDIT_ELIGIBLE_CONFIRMATION_TEXT', { name }),
        });
    }

    return true;
}

function prepareAffiliationsForEditor(
    affiliations: ArticleAffiliation[] | EditorAffiliation[],
    suggestions?: {
        matches?: Record<string, Institution[]>;
        emailSuggestions?: Array<Institution & { emailMatched: string }>;
    },
    confirmed: boolean = false,
): EditorAffiliation[] {
    let affList: EditorAffiliation[] =
        (affiliations || []).map(aff => {
            const custom = !aff.institutionId ? true : aff.custom;
            return {
                // @ts-ignore
                // eslint-disable-next-line no-nested-ternary
                confirmed: aff.submissionData ? (custom ? confirmed : true) : true,
                // @ts-ignore
                // eslint-disable-next-line no-nested-ternary
                id: aff.id || uuidv4(),
                ...aff,
                custom,
                // @ts-ignore
                id: aff.id || uuidv4(),
            };
        }) || [];

    if (!confirmed && suggestions && suggestions.emailSuggestions) {
        // @ts-ignore
        // FIXME
        affList = affList.concat(suggestions.emailSuggestions);
    }

    if (!confirmed && suggestions && suggestions.matches) {
        const affiliationsMatches = suggestions.matches;
        affList.forEach(aff => {
            // eslint-disable-next-line no-param-reassign
            aff.suggestions = affiliationsMatches[aff.affiliationId];
        });
    }

    return affList;
}

function isEmailSuggestion(
    item: EditorAffiliation | (Institution & { emailMatched: string }),
): item is Institution & { emailMatched: string } {
    return !!item.emailMatched;
}

type RejectedAccount = {
    id: string;
    code: string;
    name: string;
    rejectionReasons: string[];
};

type RejectionReasonsResult = {
    rejectionReasonAccountBalanceNotEnough: boolean;
    rejectionReasonAccountBalanceNotEnoughInstitutionName?: string;
};

function checkRejectionReasons(
    woaCode?: string,
    rejectedAccounts: RejectedAccount[] = [],
    affiliations: ArticleAffiliation[] = [],
): RejectionReasonsResult {
    const result: RejectionReasonsResult = {
        rejectionReasonAccountBalanceNotEnough: false,
    };

    if (!woaCode || !rejectedAccounts || rejectedAccounts.length === 0) {
        return result;
    }

    // Find rejected accounts that match the woaCode
    const matchingAccounts = rejectedAccounts.filter(account => account.code === woaCode);

    if (matchingAccounts.length === 0) {
        return result;
    }

    // Find the affiliation that contains the matching rejected account
    const affiliationWithRejection = affiliations.find(aff =>
        (aff as any).rejectedAccounts?.some(
            (account: RejectedAccount) =>
                account.code === woaCode && account.rejectionReasons && Array.isArray(account.rejectionReasons),
        ),
    );

    matchingAccounts.forEach(account => {
        if (account.rejectionReasons && Array.isArray(account.rejectionReasons)) {
            const hasAccountBalanceNotEnough = account.rejectionReasons.includes('ACCOUNT_BALANCE_NOT_ENOUGH');

            if (hasAccountBalanceNotEnough) {
                result.rejectionReasonAccountBalanceNotEnough = true;
                result.rejectionReasonAccountBalanceNotEnoughInstitutionName =
                    affiliationWithRejection?.institutionName;
            }
        }
    });

    return result;
}

function evaluatePriceChangeDecision({
    affiliations,
    affiliationsConfirmed,
    fundingGroupId,
    isAuthorPaid,
    isWaiverAvailable,
    journalRevenueModel,
    rejectedAccounts,
    selectedPrimaryAffiliationId,
    woaCode,
}: PriceChangeDecisionInput): PriceChangeDecisionResult {
    const eligibleAffiliations = (affiliations || []).filter(affiliation => affiliation.eligibleForOO);
    const cdlScelcAffiliations = affiliations.filter(affiliation =>
        CDL_SCELC_WORKFLOW_TYPES.includes(affiliation.workflowType as string),
    );
    const discountedAffiliations = affiliations.filter(aff => aff.eligibleForInstitutionalDiscount);
    const isFunderPaid = !isAuthorPaid && !!fundingGroupId;

    const isEligibleForFullCoverage = eligibleAffiliations.length > 0;
    const isSingleDiscountCoverage = discountedAffiliations.length === 1;
    const isEligibleForDiscountCoverage = discountedAffiliations.length > 0;
    const noDiscountCoverage = discountedAffiliations.length === 0;
    let isNormalWorkflow = cdlScelcAffiliations.length === 0;

    let primaryFundingInstitution = affiliations.filter(aff => aff.affiliationId === selectedPrimaryAffiliationId)[0]
        ?.institutionId;
    if (!primaryFundingInstitution) {
        if (eligibleAffiliations.length > 1 && eligibleAffiliations.filter(aff => aff.primary).length > 0) {
            primaryFundingInstitution = eligibleAffiliations[0].institutionId;
        } else if (eligibleAffiliations.length === 1) {
            primaryFundingInstitution = eligibleAffiliations[0].institutionId;
        }
    }
    const fundingInstitutionChanged = primaryFundingInstitution ? primaryFundingInstitution !== fundingGroupId : false;

    let priceChangeType: PriceChangeType | undefined;

    if (isAuthorPaid && isEligibleForFullCoverage) {
        priceChangeType = PriceChangeType.AUTHOR_TO_FUNDER;
        isNormalWorkflow =
            cdlScelcAffiliations.filter(aff => aff.institutionId === primaryFundingInstitution).length === 0;
    } else if (isAuthorPaid && isSingleDiscountCoverage) {
        priceChangeType = PriceChangeType.AUTHOR_TO_AUTHOR_INSTITUTIONAL_DISCOUNT;
        isNormalWorkflow = discountedAffiliations.filter(aff => aff.workflowType === NORMAL_WORKFLOW_TYPE).length === 1;
    } else if (isFunderPaid && isEligibleForFullCoverage && fundingInstitutionChanged) {
        priceChangeType = PriceChangeType.FUNDER_TO_FUNDER;
        isNormalWorkflow =
            cdlScelcAffiliations.filter(aff => aff.institutionId === primaryFundingInstitution).length === 0;
    } else if (isFunderPaid && isEligibleForDiscountCoverage) {
        priceChangeType = PriceChangeType.FUNDER_TO_AUTHOR_INSTITUTIONAL_DISCOUNT;
        isNormalWorkflow =
            discountedAffiliations.filter(aff => aff.workflowType === NORMAL_WORKFLOW_TYPE).length > 0 &&
            cdlScelcAffiliations.length === 0;
    } else if (isFunderPaid && !isEligibleForFullCoverage && noDiscountCoverage) {
        priceChangeType = PriceChangeType.FUNDER_TO_AUTHOR;
    }

    const shouldShowPriceChangeConfirmation =
        journalRevenueModel === 'OA' &&
        isNormalWorkflow &&
        !isWaiverAvailable &&
        !!priceChangeType &&
        !affiliationsConfirmed;

    const rejectionReasons = checkRejectionReasons(woaCode, rejectedAccounts, affiliations);

    return {
        eligibleAffiliations,
        priceChangeType,
        rejectionReasonAccountBalanceNotEnough: rejectionReasons.rejectionReasonAccountBalanceNotEnough,
        rejectionReasonAccountBalanceNotEnoughInstitutionName:
            rejectionReasons.rejectionReasonAccountBalanceNotEnoughInstitutionName,
        shouldShowPriceChangeConfirmation,
    };
}

export {
    EditorAffiliation,
    confirmAffiliationDeletion,
    confirmAffiliationEditing,
    isEmailSuggestion,
    prepareAffiliationsForEditor,
    evaluatePriceChangeDecision,
};
